import { InfiniteData, QueryFunctionContext, QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

import { PublicApiLog } from '@/models/Log';
import { PublicApiLogResponse } from '@/models/network';
import { publicApi } from '@/utils/api';

export type LogData = {
  logStrings: string[];
  archivedLog: string;
  isFirstChunkLoaded: boolean;
  nextAfterTimestamp?: string;
  nextBeforeTimestamp?: string;
  setArchivedLog(log: string): void;
  setFirstChunkLoaded(isLoaded: boolean): void;
  setNextAfterTimestamp(timeStamp: string): void;
  setNextBeforeTimestamp(timeStamp: string): void;
  setLogStrings: Dispatch<SetStateAction<string[]>>;
};

export type BuildData = {
  appSlug: string;
  buildSlug: string;
  isBuildRunning: boolean;
  isBuildStarted: boolean;
  publicAccess?: boolean;
  requestStartOfLog: boolean;
};

export type FetchLogsProps = {
  buildData: BuildData;
  isEnabled?: boolean;
  logData: LogData;
};

type FetchLogsResponse = {
  isFetching: boolean;
  isArchived: boolean;
  expiringRawLogUrl?: string;
  fetchPage(): Promise<unknown>;
  errorStatus: number | undefined;
  nextBeforeTimestamp: string | undefined;
};

const useFetchLog = (
  { buildData, isEnabled, logData }: FetchLogsProps,
  pageDirection: 'prevPage' | 'nextPage',
): FetchLogsResponse => {
  const { appSlug, buildSlug, publicAccess } = buildData;
  const { setFirstChunkLoaded, setLogStrings } = logData;
  const [nextBeforeTimestamp, setNextBeforeTimestamp] = useState<string | undefined>(undefined);
  const [nextAfterTimestamp, setNextAfterTimestamp] = useState<string | undefined>(undefined);
  const [isArchived, setArchived] = useState<boolean>(false);

  const fetchData = async ({ pageParam }: QueryFunctionContext<QueryKey, string | undefined>) => {
    return publicApi.get<PublicApiLogResponse<PublicApiLog[]>>(
      `/v0.1/apps/${appSlug}/builds/${buildSlug}/log?${pageParam || 'after_timestamp='}`,
      {
        publicAccess,
      },
    );
  };

  const updateLogData = useCallback(
    (response: InfiniteData<AxiosResponse<PublicApiLogResponse<PublicApiLog[]>>>) => {
      const { data } = pageDirection === 'nextPage' ? response.pages[response.pages.length - 1] : response.pages[0];

      setNextAfterTimestamp(data.nextAfterTimestamp || '');
      if (data.nextBeforeTimestamp !== '0001-01-01T00:00:00Z') {
        setNextBeforeTimestamp(data.nextBeforeTimestamp || '');
      }

      if (data.isArchived) {
        setArchived(true);
      }

      if (!data.logChunks) {
        return;
      }

      const isFirstChunkLoaded = data.logChunks.some(({ position }) => position === 1);
      if (isFirstChunkLoaded) {
        setFirstChunkLoaded(true);
      }

      setLogStrings((currentLogStrings) => {
        const newLogStrings = [...currentLogStrings];

        data.logChunks.forEach(({ chunk, position }) => {
          newLogStrings[position] = chunk;
        });

        return newLogStrings;
      });
    },
    [pageDirection, setFirstChunkLoaded, setLogStrings],
  );

  const { data, error, isFetchingNextPage, isFetchingPreviousPage, fetchPreviousPage, fetchNextPage } =
    useInfiniteQuery<
      AxiosResponse<PublicApiLogResponse<PublicApiLog[]>>,
      AxiosError,
      InfiniteData<AxiosResponse<PublicApiLogResponse<PublicApiLog[]>>, string | undefined>,
      QueryKey,
      string | undefined
    >({
      queryKey: ['build-log', buildSlug],
      queryFn: fetchData,
      initialPageParam: undefined,
      getNextPageParam: () => {
        return nextAfterTimestamp ? `after_timestamp=${nextAfterTimestamp}` : undefined;
      },
      getPreviousPageParam: () => {
        return nextBeforeTimestamp ? `before_timestamp=${nextBeforeTimestamp}` : undefined;
      },
      enabled: isEnabled,
      retry: false,
    });

  useEffect(() => {
    if (data) {
      updateLogData(data);
    }
  }, [data, updateLogData]);

  return {
    isFetching: pageDirection === 'nextPage' ? isFetchingNextPage : isFetchingPreviousPage,
    fetchPage: pageDirection === 'nextPage' ? fetchNextPage : fetchPreviousPage,
    errorStatus: error?.response?.status,
    nextBeforeTimestamp,
    isArchived,
  };
};

export default useFetchLog;
